// frontend/src/contexts/BrowserSessionContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import api from '../services/api' // Import your API service

export const BrowserSessionContext = createContext({})

export const BrowserSessionProvider = ({ children }) => {
  const [activeSessions, setActiveSessions] = useState({
    conversational: 0,
    automation: 0,
    total: 0
  })
  const [activeSession, setActiveSession] = useState(null)
  const [sessionHistory, setSessionHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { user, isAuthenticated } = useAuth()

  // Helper function to map backend session to local format
  const mapBackendToLocal = (backendSession) => {
    return {
      id: backendSession.id,
      type: backendSession.session_type,
      title: backendSession.title,
      url: backendSession.url || '',
      status: backendSession.status?.toLowerCase() || 'active',
      createdAt: backendSession.created_at,
      updatedAt: backendSession.updated_at,
      userId: backendSession.user_id,
      description: backendSession.description || '',
      settings: backendSession.settings || {},
      metadata: {
        testCases: [],
        executionResults: [],
        screenshots: [],
        logs: []
      }
    }
  }

  // Helper function to map local session to backend format
  const mapLocalToBackend = (localSession, updates = {}) => {
    return {
      title: updates.title || localSession.title,
      session_type: updates.type || localSession.type,
      description: updates.description || localSession.description || '',
      url: updates.url || localSession.url || '',
      status: (updates.status || localSession.status)?.toUpperCase(),
      settings: { 
        ...localSession.settings, 
        ...updates.settings,
        // Store metadata in settings for now (you might want a separate metadata table later)
        metadata: {
          ...localSession.metadata,
          ...updates.metadata
        }
      }
    }
  }

  // Load sessions from backend and localStorage on mount
  useEffect(() => {
    const loadSessions = async () => {
      if (!isAuthenticated || !user) return

      setLoading(true)
      setError(null)

      try {
        // Try to load from backend first
        const backendSessions = await api.getSessions()
        const localSessions = backendSessions.map(mapBackendToLocal)
        
        setSessionHistory(localSessions)
        
        // Update active session counts
        updateActiveSessionCounts(localSessions)
        
        // Cache in localStorage as backup
        try {
          localStorage.setItem(`sessions_${user.id}`, JSON.stringify(localSessions))
        } catch (storageError) {
          console.warn('Failed to cache sessions in localStorage:', storageError)
        }

      } catch (apiError) {
        console.error('Failed to load sessions from backend:', apiError)
        
        // Fallback to localStorage if backend fails
        try {
          const savedSessions = localStorage.getItem(`sessions_${user.id}`)
          if (savedSessions) {
            const sessions = JSON.parse(savedSessions)
            setSessionHistory(sessions)
            updateActiveSessionCounts(sessions)
            console.log('Loaded sessions from localStorage as fallback')
          }
        } catch (storageError) {
          console.error('Failed to load sessions from localStorage:', storageError)
          setError('Failed to load sessions from both backend and local storage')
        }
      } finally {
        setLoading(false)
      }
    }

    loadSessions()
  }, [isAuthenticated, user])

  // Helper function to update active session counts
  const updateActiveSessionCounts = (sessions) => {
    const conversational = sessions.filter(s => s.type === 'conversational' && s.status === 'active').length
    const automation = sessions.filter(s => s.type === 'automation' && s.status === 'active').length
    
    setActiveSessions({
      conversational,
      automation,
      total: conversational + automation
    })
  }

  // Save sessions to localStorage whenever sessionHistory changes (backup only)
  useEffect(() => {
    if (isAuthenticated && user && sessionHistory.length > 0) {
      try {
        // Only store lightweight version in localStorage (without heavy metadata)
        const lightSessions = sessionHistory.map(session => ({
          ...session,
          metadata: {
            ...session.metadata,
            screenshots: [], // Remove base64 data
            logs: session.metadata.logs?.slice(-10) || [] // Keep only recent logs
          }
        }))
        localStorage.setItem(`sessions_${user.id}`, JSON.stringify(lightSessions))
      } catch (error) {
        console.warn('Failed to backup sessions to localStorage:', error)
      }
    }
  }, [sessionHistory, isAuthenticated, user])

  const createSession = async (type, title, options = {}) => {
    if (!isAuthenticated) {
      throw new Error('User must be authenticated to create sessions')
    }

    setLoading(true)
    setError(null)

    try {
      // Create session in backend first
      const backendSessionData = {
        title,
        session_type: type,
        description: options.description || `${type} session for ${title}`,
        url: options.url || '',
        settings: {
          ...options.settings,
          testCases: options.testCases || [],
          metadata: {
            testCases: options.testCases || [],
            executionResults: [],
            screenshots: [],
            logs: []
          }
        }
      }

      console.log('Creating session in backend:', backendSessionData)
      const backendSession = await api.createSession(backendSessionData)
      console.log('Backend session created:', backendSession)

      // Convert to local format
      const newSession = mapBackendToLocal(backendSession)
      
      // Add any additional metadata from options
      newSession.metadata = {
        testCases: options.testCases || [],
        executionResults: [],
        screenshots: [],
        logs: []
      }

      // Update local state
      setSessionHistory(prev => [newSession, ...prev])
      setActiveSession(newSession)
      
      // Update counts
      setActiveSessions(prev => ({
        ...prev,
        [type]: prev[type] + 1,
        total: prev.total + 1
      }))

      console.log('Session created successfully:', newSession)
      return newSession

    } catch (error) {
      console.error('Failed to create session:', error)
      setError(`Failed to create session: ${error.message}`)
      
      // Optional: Create local-only session as fallback
      const fallbackSession = {
        id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type,
        title: title + ' (Local Only)',
        url: options.url || '',
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        userId: user?.id,
        isLocalOnly: true, // Flag to indicate this is not synced
        metadata: {
          testCases: options.testCases || [],
          executionResults: [],
          screenshots: [],
          logs: []
        }
      }
      
      setSessionHistory(prev => [fallbackSession, ...prev])
      setActiveSession(fallbackSession)
      
      console.log('Created fallback local session:', fallbackSession)
      return fallbackSession

    } finally {
      setLoading(false)
    }
  }

  const updateSession = async (sessionId, updates) => {
    setError(null)
    
    try {
      // Find the session
      const session = sessionHistory.find(s => s.id === sessionId)
      if (!session) {
        throw new Error('Session not found')
      }

      // Update in backend if it's not a local-only session
      if (!session.isLocalOnly) {
        const backendData = mapLocalToBackend(session, updates)
        console.log('Updating session in backend:', sessionId, backendData)
        await api.updateSession(sessionId, backendData)
      }

      // Update local state
      const updatedSession = {
        ...session,
        ...updates,
        updatedAt: new Date().toISOString()
      }

      setSessionHistory(prev => 
        prev.map(s => s.id === sessionId ? updatedSession : s)
      )

      // Update active session if it's the one being updated
      if (activeSession?.id === sessionId) {
        setActiveSession(updatedSession)
      }

      // Update counts if status changed
      if (updates.status) {
        updateActiveSessionCounts(sessionHistory.map(s => 
          s.id === sessionId ? updatedSession : s
        ))
      }

      console.log('Session updated successfully:', sessionId)

    } catch (error) {
      console.error('Failed to update session:', error)
      setError(`Failed to update session: ${error.message}`)
      
      // Still update locally even if backend fails
      setSessionHistory(prev => 
        prev.map(session => 
          session.id === sessionId 
            ? { ...session, ...updates, updatedAt: new Date().toISOString() }
            : session
        )
      )
    }
  }

  const closeSession = async (sessionId) => {
    await updateSession(sessionId, { status: 'closed' })
    
    // Clear active session if it's the one being closed
    if (activeSession?.id === sessionId) {
      setActiveSession(null)
    }
  }

  const deleteSession = async (sessionId) => {
    setError(null)
    
    try {
      // Find the session
      const session = sessionHistory.find(s => s.id === sessionId)
      if (!session) {
        throw new Error('Session not found')
      }

      // Delete from backend if it's not a local-only session
      if (!session.isLocalOnly) {
        console.log('Deleting session from backend:', sessionId)
        await api.deleteSession(sessionId)
      }

      // Remove from local state
      setSessionHistory(prev => prev.filter(s => s.id !== sessionId))
      
      // Update counts if it was active
      if (session.status === 'active') {
        setActiveSessions(prev => ({
          ...prev,
          [session.type]: Math.max(0, prev[session.type] - 1),
          total: Math.max(0, prev.total - 1)
        }))
      }

      // Clear active session if it's the one being deleted
      if (activeSession?.id === sessionId) {
        setActiveSession(null)
      }

      console.log('Session deleted successfully:', sessionId)

    } catch (error) {
      console.error('Failed to delete session:', error)
      setError(`Failed to delete session: ${error.message}`)
    }
  }

  const getSessionById = (sessionId) => {
    return sessionHistory.find(session => session.id === sessionId)
  }

  const getSessionsByType = (type) => {
    return sessionHistory.filter(session => session.type === type)
  }

  const addTestResult = async (sessionId, testResult) => {
    const session = getSessionById(sessionId)
    if (!session) return

    const updatedMetadata = {
      ...session.metadata,
      executionResults: [
        ...(session.metadata.executionResults || []),
        testResult
      ]
    }

    await updateSession(sessionId, { metadata: updatedMetadata })
  }

  const addScreenshot = async (sessionId, screenshot) => {
    const session = getSessionById(sessionId)
    if (!session) return

    const updatedMetadata = {
      ...session.metadata,
      screenshots: [
        ...(session.metadata.screenshots || []),
        screenshot
      ]
    }

    await updateSession(sessionId, { metadata: updatedMetadata })
  }

  const clearAllSessions = async () => {
    setError(null)
    
    try {
      // Clear from backend (optional - you might want to keep them but mark as archived)
      const activeSessions = sessionHistory.filter(s => !s.isLocalOnly)
      for (const session of activeSessions) {
        await api.deleteSession(session.id)
      }
    } catch (error) {
      console.error('Failed to clear sessions from backend:', error)
    }

    // Clear local state
    setSessionHistory([])
    setActiveSession(null)
    setActiveSessions({ conversational: 0, automation: 0, total: 0 })
    
    // Clear localStorage
    if (user) {
      localStorage.removeItem(`sessions_${user.id}`)
    }
  }

  // Sync a local-only session to backend (for retry functionality)
  const syncLocalSession = async (sessionId) => {
    const session = sessionHistory.find(s => s.id === sessionId && s.isLocalOnly)
    if (!session) return

    try {
      const backendData = mapLocalToBackend(session)
      const backendSession = await api.createSession(backendData)
      
      // Replace local session with backend session
      const syncedSession = {
        ...mapBackendToLocal(backendSession),
        metadata: session.metadata // Preserve local metadata
      }
      
      setSessionHistory(prev => 
        prev.map(s => s.id === sessionId ? syncedSession : s)
      )
      
      if (activeSession?.id === sessionId) {
        setActiveSession(syncedSession)
      }
      
      console.log('Session synced to backend:', syncedSession)
      return syncedSession
      
    } catch (error) {
      console.error('Failed to sync session:', error)
      throw error
    }
  }

  const value = {
    // State
    activeSessions,
    activeSession,
    sessionHistory,
    loading,
    error,
    
    // Actions
    createSession,
    updateSession,
    closeSession,
    deleteSession,
    getSessionById,
    getSessionsByType,
    addTestResult,
    addScreenshot,
    clearAllSessions,
    syncLocalSession,
    
    // Setters
    setActiveSession,
    
    // Helper function to clear errors
    clearError: () => setError(null)
  }

  return (
    <BrowserSessionContext.Provider value={value}>
      {children}
    </BrowserSessionContext.Provider>
  )
}

export const useBrowserSession = () => {
  const context = useContext(BrowserSessionContext)
  if (!context) {
    throw new Error('useBrowserSession must be used within a BrowserSessionProvider')
  }
  return context
}

export default BrowserSessionContext